"use client";

import {
  type HTMLAttributes,
  type FC,
  type ReactNode,
  type ReactElement,
  isValidElement,
} from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { isString, isStringNumber, mergeClasses } from "@huub-web/utils";

import type { ClassNameProp } from "../../props";
import { badgeStyle } from "./Badge.css";
import { typography } from "../../theme/css/typography.css";

export type BadgeProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> &
  ClassNameProp &
  RecipeVariants<typeof badgeStyle> & {
    value: number | string | ReactNode;
    max?: number;
    limit?: (value: number, max: number) => number | string | ReactElement<any>;
    inline?: boolean;
  };

/**
 * A badge is a small status descriptor for UI elements.
 */
export const Badge: FC<BadgeProps> = ({
  className,
  value,
  variant,
  max = 99,
  size = "xs",
  inline = false,
  offset = "default",
  limit = (value: number, max: number): number | string =>
    value <= max ? value : `${max}+`,
  ...props
}) => {
  const parsed = (() => {
    if (typeof value === "number") {
      return limit(value, max);
    }
    return value;
  })();

  return (
    <div
      className={mergeClasses(
        badgeStyle({
          offset: !inline ? offset : undefined,
          variant,
          inline,
          icon: isValidElement(parsed),
          isString: isString(parsed) && !isStringNumber(parsed),
          size,
        }),
        typography.bodyXS,
        className,
      )}
      {...props}
    >
      {parsed}
    </div>
  );
};
